import { base44 } from '@/api/base44Client';
import { notifyMilestone } from '@/components/notifications/NotificationHelper';

export const POINT_VALUES = {
  scan: 10,
  review: 20,
  post: 15,
  comment: 5,
  share: 8,
  group_join: 25,
  invite: 50,
};

export const BADGES = {
  first_scan: { emoji: '🔍', name: 'Primer Escaneo', condition: 'scan', count: 1 },
  scanner_pro: { emoji: '📊', name: 'Escáner Pro', condition: 'scan', count: 50 },
  reviewer: { emoji: '⭐', name: 'Crítico', condition: 'review', count: 10 },
  social_butterfly: { emoji: '🦋', name: 'Mariposa Social', condition: 'post', count: 20 },
  community_leader: { emoji: '👑', name: 'Líder Comunitario', condition: 'group_join', count: 5 },
  ambassador: { emoji: '🌟', name: 'Embajador', condition: 'invite', count: 10 },
};

export const LEVELS = [
  { level: 1, name: 'Principiante', minPoints: 0, maxPoints: 99, color: 'text-gray-600' },
  { level: 2, name: 'Aprendiz', minPoints: 100, maxPoints: 249, color: 'text-blue-600' },
  { level: 3, name: 'Conocedor', minPoints: 250, maxPoints: 499, color: 'text-green-600' },
  { level: 4, name: 'Experto', minPoints: 500, maxPoints: 999, color: 'text-purple-600' },
  { level: 5, name: 'Maestro', minPoints: 1000, maxPoints: 1999, color: 'text-amber-600' },
  { level: 6, name: 'Leyenda', minPoints: 2000, maxPoints: Infinity, color: 'text-red-600' },
];

export const MILESTONES = [
  { id: 'points_100', name: 'Primeros 100 puntos', type: 'total_points', target: 100, reward: 50, icon: '🎯' },
  { id: 'points_500', name: '500 puntos acumulados', type: 'total_points', target: 500, reward: 100, icon: '🏆' },
  { id: 'points_1000', name: '1000 puntos acumulados', type: 'total_points', target: 1000, reward: 200, icon: '💎' },
  { id: 'points_2000', name: '2000 puntos acumulados', type: 'total_points', target: 2000, reward: 500, icon: '👑' },
  { id: 'reviews_5', name: '5 reseñas escritas', type: 'action_count', action: 'review', target: 5, reward: 75, icon: '⭐' },
  { id: 'reviews_10', name: '10 reseñas escritas', type: 'action_count', action: 'review', target: 10, reward: 150, icon: '🌟' },
  { id: 'scans_20', name: '20 productos escaneados', type: 'action_count', action: 'scan', target: 20, reward: 100, icon: '🔍' },
  { id: 'scans_50', name: '50 productos escaneados', type: 'action_count', action: 'scan', target: 50, reward: 250, icon: '🎖️' },
  { id: 'posts_5', name: '5 posts compartidos', type: 'action_count', action: 'post', target: 5, reward: 80, icon: '✍️' },
  { id: 'groups_3', name: 'Unido a 3 grupos', type: 'action_count', action: 'group_join', target: 3, reward: 100, icon: '👥' },
];

export function getLevelInfo(totalPoints) {
  const currentLevel = LEVELS.find(l => totalPoints >= l.minPoints && totalPoints <= l.maxPoints) || LEVELS[0];
  const nextLevel = LEVELS.find(l => l.level === currentLevel.level + 1);
  
  const progressInLevel = totalPoints - currentLevel.minPoints;
  const pointsNeededForLevel = nextLevel ? (nextLevel.minPoints - currentLevel.minPoints) : 0;
  const progressPercentage = nextLevel ? (progressInLevel / pointsNeededForLevel) * 100 : 100;
  
  return {
    currentLevel,
    nextLevel,
    progressInLevel,
    pointsNeededForLevel,
    progressPercentage: Math.min(progressPercentage, 100),
    pointsToNextLevel: nextLevel ? (nextLevel.minPoints - totalPoints) : 0
  };
}

export async function awardPoints(userId, action, points, description, referenceId = null) {
  try {
    // Get or create user points
    let userPoints = await base44.entities.UserPoints.filter({ user_id: userId });
    
    if (userPoints.length === 0) {
      userPoints = await base44.entities.UserPoints.create({
        user_id: userId,
        total_points: 0,
        available_points: 0,
        level: 1,
        badges: [],
        milestones_completed: []
      });
    } else {
      userPoints = userPoints[0];
    }

    // Create transaction
    await base44.entities.PointTransaction.create({
      user_id: userId,
      points,
      action,
      description,
      reference_id: referenceId
    });

    // Update points
    const newTotalPoints = userPoints.total_points + points;
    const newAvailablePoints = userPoints.available_points + points;
    const levelInfo = getLevelInfo(newTotalPoints);

    await base44.entities.UserPoints.update(userPoints.id, {
      total_points: newTotalPoints,
      available_points: newAvailablePoints,
      level: levelInfo.currentLevel.level
    });

    // Check for badge unlocks
    await checkBadges(userId, action);

    // Check for milestones
    await checkMilestones(userId, newTotalPoints);

    return { success: true, points };
  } catch (error) {
    console.error('Error awarding points:', error);
    return { success: false, error };
  }
}

async function checkBadges(userId, action) {
  try {
    const userPoints = (await base44.entities.UserPoints.filter({ user_id: userId }))[0];
    if (!userPoints) return;

    const transactions = await base44.entities.PointTransaction.filter({ 
      user_id: userId, 
      action 
    });

    for (const [badgeKey, badge] of Object.entries(BADGES)) {
      if (badge.condition === action && transactions.length >= badge.count) {
        if (!userPoints.badges.includes(badge.emoji)) {
          await base44.entities.UserPoints.update(userPoints.id, {
            badges: [...userPoints.badges, badge.emoji]
          });
        }
      }
    }
  } catch (error) {
    console.error('Error checking badges:', error);
  }
}

export async function checkMilestones(userId, totalPoints) {
  try {
    const existingPoints = await base44.entities.UserPoints.filter({ user_id: userId });
    const userPoints = existingPoints[0];
    if (!userPoints) return;

    const completedMilestones = userPoints.milestones_completed || [];
    const transactions = await base44.entities.PointTransaction.filter({ user_id: userId });

    for (const milestone of MILESTONES) {
      if (completedMilestones.includes(milestone.id)) continue;

      let achieved = false;

      if (milestone.type === 'total_points') {
        achieved = totalPoints >= milestone.target;
      } else if (milestone.type === 'action_count') {
        const actionCount = transactions.filter(t => t.action === milestone.action).length;
        achieved = actionCount >= milestone.target;
      }

      if (achieved) {
        // Award milestone reward
        await base44.entities.PointTransaction.create({
          user_id: userId,
          points: milestone.reward,
          action: 'milestone',
          description: `Hito alcanzado: ${milestone.name}`
        });

        await base44.entities.UserPoints.update(userPoints.id, {
          milestones_completed: [...completedMilestones, milestone.id],
          total_points: totalPoints + milestone.reward,
          available_points: (userPoints.available_points || 0) + milestone.reward
        });

        // Notify user about milestone achievement
        await notifyMilestone({
          userId,
          milestoneName: milestone.name,
          reward: milestone.reward
        });
      }
    }
  } catch (error) {
    console.error('Error checking milestones:', error);
  }
}

export async function deductPoints(userId, points, action, description) {
  try {
    const userPoints = (await base44.entities.UserPoints.filter({ user_id: userId }))[0];
    
    if (!userPoints || userPoints.available_points < points) {
      return { success: false, error: 'Puntos insuficientes' };
    }

    await base44.entities.PointTransaction.create({
      user_id: userId,
      points: -points,
      action: action,
      description
    });

    await base44.entities.UserPoints.update(userPoints.id, {
      available_points: userPoints.available_points - points
    });

    return { success: true };
  } catch (error) {
    console.error('Error deducting points:', error);
    return { success: false, error };
  }
}